// Person ID Management
//
// This module provides a unified interface for managing user identity across analytics providers.
// It abstracts PostHog's distinct_id for consistent person identification.
//
// RESPONSIBILITIES:
// - Retrieve PostHog's distinct_id when available
// - Provide consistent person identification across all analytics providers
//
// USAGE:
// import { getPersonId, setPostHogInstanceGetter } from './person-id';
// const personId = getPersonId(); // Returns PostHog distinct_id or null
//
// GDPR COMPLIANCE:
// - Uses PostHog's built-in distinct_id (already GDPR-compliant)
// - Returns null when PostHog is not available (no tracking until user consent/initialization)

import { getConsentMode } from '../services/consentMode';
import { logger } from '../utils/core/logger';

// PostHog instance interface for distinct_id access
interface PostHogInstanceWithDistinctId {
    get_distinct_id?: () => string;
}

// PostHog instance getter (set by PostHogProvider)
let getPostHogInstance: (() => PostHogInstanceWithDistinctId | null) | null = null;

/**
 * Set the PostHog instance getter (called from PostHogProvider)
 */
export const setPostHogInstanceGetter = (getter: () => PostHogInstanceWithDistinctId | null) => {
    getPostHogInstance = getter;
};

/**
 * Get PostHog distinct_id from PostHog instance
 *
 * @returns {string | null} PostHog distinct_id or null if unavailable
 */
export const getPersonId = (): string | null => {
    if (getConsentMode() !== 'full') return null;
    try {
        if (getPostHogInstance) {
            const posthog = getPostHogInstance();
            if (posthog?.get_distinct_id) {
                const distinctId = posthog.get_distinct_id();
                if (distinctId && distinctId !== '$posthog_cookieless') {
                    logger.debug('🆔 Using PostHog distinct_id:', distinctId);
                    return distinctId;
                }
            }
        }

        // No window.posthog fallback. In MAIN world (chrome-extension),
        // window.posthog is the host page's instance, not ours.
    } catch (error) {
        logger.debug('Could not retrieve PostHog distinct_id:', error);
    }

    logger.debug('🆔 PostHog distinct_id not available');
    return null;
};

/**
 * Check if PostHog distinct_id is available
 */
export const hasPostHogDistinctId = (): boolean => {
    return getPersonId() !== null;
};
